/**
 * AI Trial Room · Phase 2 · Live Camera Try-On
 * Uses MediaPipe FaceLandmarker (468 face points) — 100% browser-side, free.
 * Overlays admin-uploaded jewelry/hairstyle/makeup product images onto face.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FilesetResolver, FaceLandmarker } from '@mediapipe/tasks-vision';
import { toast } from 'sonner';
import { Camera, Download, X, Sparkles, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import AIAdviceButton from './AIAdviceButton';
import AIBestMatchButton from './AIBestMatchButton';

const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';
const WASM_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';

// Anchor landmark indices (MediaPipe 468-point FaceMesh)
const IDX = {
  leftEar: 234, rightEar: 454,
  noseBridge: 168, chinBottom: 152,
  leftEyeOuter: 33, rightEyeOuter: 263,
  foreheadTop: 10, leftTemple: 21, rightTemple: 251,
  lipTop: 0, lipBottom: 17, lipLeft: 61, lipRight: 291,
  leftJaw: 172, rightJaw: 397,
};

const classifyOverlay = (category, subcategory) => {
  const sub = (subcategory || '').toLowerCase();
  if (category === 'jewelry') {
    if (/ear/.test(sub)) return 'earrings';
    if (/necklace|neck|chain|pendant/.test(sub)) return 'necklace';
    if (/sunglass|glass|shades/.test(sub)) return 'sunglasses';
    return 'earrings';
  }
  if (category === 'salon') {
    if (/lip|lipstick/.test(sub)) return 'lips';
    if (/hair|wig|hairstyle/.test(sub)) return 'hair';
    return 'hair';
  }
  return 'earrings';
};

const drawOverlay = (ctx, face, w, h, img, kind) => {
  if (!img || !img.complete || img.naturalWidth === 0) return;
  const lm = (i) => ({ x: face[i].x * w, y: face[i].y * h });
  ctx.save();
  if (kind === 'earrings') {
    const lE = lm(IDX.leftEar), rE = lm(IDX.rightEar);
    const s = Math.abs(rE.x - lE.x) * 0.14;
    ctx.drawImage(img, lE.x - s / 2, lE.y - s / 4, s, s);
    ctx.drawImage(img, rE.x - s / 2, rE.y - s / 4, s, s);
  } else if (kind === 'necklace') {
    const chin = lm(IDX.chinBottom), lJ = lm(IDX.leftJaw), rJ = lm(IDX.rightJaw);
    const width = Math.abs(rJ.x - lJ.x) * 1.4;
    const height = width * 0.55;
    ctx.drawImage(img, chin.x - width / 2, chin.y + height * 0.1, width, height);
  } else if (kind === 'sunglasses') {
    const lEye = lm(IDX.leftEyeOuter), rEye = lm(IDX.rightEyeOuter), nose = lm(IDX.noseBridge);
    const width = Math.abs(rEye.x - lEye.x) * 1.35;
    const height = width * 0.38;
    ctx.drawImage(img, nose.x - width / 2, nose.y - height * 0.45, width, height);
  } else if (kind === 'hair') {
    const fore = lm(IDX.foreheadTop), lT = lm(IDX.leftTemple), rT = lm(IDX.rightTemple);
    const width = Math.abs(rT.x - lT.x) * 1.7;
    const height = width * 1.15;
    ctx.drawImage(img, fore.x - width / 2, fore.y - height * 0.85, width, height);
  } else if (kind === 'lips') {
    const lipL = lm(IDX.lipLeft), lipR = lm(IDX.lipRight), lipT = lm(IDX.lipTop), lipB = lm(IDX.lipBottom);
    const width = Math.abs(lipR.x - lipL.x) * 1.15;
    const height = Math.abs(lipB.y - lipT.y) * 1.6;
    ctx.globalAlpha = 0.55;
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(img, lipL.x - width * 0.08, lipT.y - height * 0.15, width, height);
  }
  ctx.restore();
};

// MediaPipe FaceMesh outer lip contour (20 points, in order)
const LIP_OUTER = [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146];

const drawColorFills = (ctx, face, w, h, lipColor, hairColor) => {
  const lm = (i) => ({ x: face[i].x * w, y: face[i].y * h });
  // Lipstick color fill
  if (lipColor) {
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = lipColor;
    ctx.beginPath();
    LIP_OUTER.forEach((idx, i) => {
      const p = lm(idx);
      if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
    });
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  // Hair color tint — approximation using an arc above forehead
  if (hairColor) {
    const fore = lm(IDX.foreheadTop), lT = lm(IDX.leftTemple), rT = lm(IDX.rightTemple);
    const width = Math.abs(rT.x - lT.x) * 1.55;
    const height = width * 1.05;
    const cx = fore.x, cy = fore.y - height * 0.15;
    ctx.save();
    ctx.globalAlpha = 0.35;
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = hairColor;
    ctx.beginPath();
    ctx.ellipse(cx, cy, width / 2, height / 2, 0, Math.PI, 0);
    ctx.lineTo(rT.x + width * 0.05, rT.y);
    ctx.lineTo(lT.x - width * 0.05, lT.y);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
};

const TrialRoomLiveCamera = ({ category, categoryTitle, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const landmarkerRef = useRef(null);
  const imgRef = useRef(null);
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [aiPicks, setAiPicks] = useState([]);
  const [lipColor, setLipColor] = useState('');
  const [hairColor, setHairColor] = useState('');
  const [showBefore, setShowBefore] = useState(false);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('🔄 Loading AI model...');

  // Load products + landmarker
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pub, mine] = await Promise.all([
          api.get(`/trial-room/products?category=${category}`),
          api.get(`/trial-room/my-assigned-products?category=${category}`).catch(() => ({ data: { products: [] } })),
        ]);
        if (cancelled) return;
        setProducts([...(mine.data?.products || []), ...(pub.data?.products || [])]);
        setStatus('🔄 Loading MediaPipe model...');
        const filesetResolver = await FilesetResolver.forVisionTasks(WASM_URL);
        const fl = await FaceLandmarker.createFromOptions(filesetResolver, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          runningMode: 'VIDEO', numFaces: 1, outputFaceBlendshapes: false,
        });
        if (cancelled) { fl.close(); return; }
        landmarkerRef.current = fl;
        setStatus('📹 Requesting camera...');
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setReady(true);
        setStatus('');
      } catch (e) {
        if (!cancelled) {
          const msg = e.name === 'NotAllowedError' ? 'Camera permission denied. Please allow access.'
                    : e.name === 'NotFoundError' ? 'No camera found on this device.'
                    : `Failed: ${e.message || e}`;
          setErr(msg);
          setStatus('');
          toast.error(`❌ ${msg}`);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (landmarkerRef.current) { try { landmarkerRef.current.close(); } catch (_) {} }
    };
  }, [category]);

  // Preload selected product image
  useEffect(() => {
    if (!selected) { imgRef.current = null; return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imgRef.current = img; };
    img.onerror = () => { imgRef.current = null; toast.error('Product image load failed'); };
    img.src = selected.image_url;
  }, [selected]);

  // Detection loop
  useEffect(() => {
    if (!ready) return;
    const loop = () => {
      const video = videoRef.current, canvas = canvasRef.current;
      if (video && canvas && video.videoWidth > 0 && landmarkerRef.current) {
        if (canvas.width !== video.videoWidth) { canvas.width = video.videoWidth; canvas.height = video.videoHeight; }
        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.scale(-1, 1); // mirror for selfie
        ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
        ctx.restore();
        try {
          const res = landmarkerRef.current.detectForVideo(video, performance.now());
          const face = res?.faceLandmarks?.[0];
          if (!showBefore && face && ((selected && imgRef.current) || lipColor || hairColor)) {
            // Mirror face landmark X to match mirrored video
            const mirrored = face.map((p) => ({ x: 1 - p.x, y: p.y, z: p.z }));
            if (selected && imgRef.current) {
              const kind = classifyOverlay(category, selected.subcategory);
              drawOverlay(ctx, mirrored, canvas.width, canvas.height, imgRef.current, kind);
            }
            if (category === 'salon' && (lipColor || hairColor)) {
              drawColorFills(ctx, mirrored, canvas.width, canvas.height, lipColor, hairColor);
            }
          }
        } catch (_) { /* silent frame skip */ }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [ready, selected, category, lipColor, hairColor, showBefore]);

  const snapshot = useCallback(() => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `TrialRoom-${category}-${Date.now()}.jpg`; a.click();
      URL.revokeObjectURL(url);
      toast.success('✅ Snapshot saved!');
    }, 'image/jpeg', 0.92);
  }, [category]);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="trial-room-camera">
      <div className="p-3 bg-slate-950 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">🪞 Live Try-On</div>
          <h3 className="text-sm md:text-base font-black text-white">{categoryTitle || category}</h3>
        </div>
        <div className="flex items-center gap-2">
          {ready && (
            <>
              <AIAdviceButton canvasRef={canvasRef} category={category} product={selected} lipColor={lipColor} hairColor={hairColor} />
              <button
                onMouseDown={() => setShowBefore(true)}
                onMouseUp={() => setShowBefore(false)}
                onMouseLeave={() => setShowBefore(false)}
                onTouchStart={() => setShowBefore(true)}
                onTouchEnd={() => setShowBefore(false)}
                onClick={() => setShowBefore((v) => !v)}
                data-testid="camera-before-after-btn"
                className={`px-3 py-1.5 rounded-lg text-xs font-black flex items-center gap-1 border-2 transition ${showBefore ? 'bg-white text-black border-white' : 'bg-transparent text-white border-white/40 hover:border-white'}`}>
                {showBefore ? '👁️ BEFORE' : '✨ AFTER'}
              </button>
              <button onClick={snapshot} data-testid="camera-snapshot-btn" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-black flex items-center gap-1"><Download className="w-3 h-3" /> Snapshot</button>
            </>
          )}
          <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center relative bg-black">
        <video ref={videoRef} playsInline muted style={{ display: 'none' }} />
        <canvas ref={canvasRef} className="max-w-full max-h-full" style={{ transform: 'scaleX(1)' }} />
        {!ready && !err && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-black/60">
            <Loader2 className="w-12 h-12 animate-spin text-cyan-400" />
            <div className="mt-3 text-sm">{status || 'Loading...'}</div>
            <div className="mt-1 text-[10px] text-slate-400">First load takes ~5-10 sec (downloading AI model, cached after)</div>
          </div>
        )}
        {err && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6 text-center">
            <div className="text-4xl mb-2">📵</div>
            <div className="text-base font-bold text-red-300">{err}</div>
            <div className="text-xs text-slate-400 mt-2 max-w-sm">Camera access chahiye MediaPipe detection ke liye. Browser settings mein permission enable karein.</div>
          </div>
        )}
      </div>

      {/* Product picker strip */}
      <div className="bg-slate-950 border-t border-white/10 p-2 space-y-1.5">
        {/* Salon color pickers */}
        {category === 'salon' && (
          <div className="flex flex-wrap gap-2 items-center justify-center">
            <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-2 py-1 border border-pink-400/30">
              <label className="text-[10px] font-bold text-pink-300">💄 Lipstick:</label>
              <input type="color" value={lipColor || '#c73866'} onChange={(e) => setLipColor(e.target.value)} data-testid="camera-lip-color" className="w-7 h-7 rounded cursor-pointer bg-transparent border-0" />
              {[{ c: '#c73866', l: 'Red' }, { c: '#7d1e3f', l: 'Wine' }, { c: '#e8998f', l: 'Nude' }, { c: '#ff6b9d', l: 'Pink' }, { c: '#8b4a5c', l: 'Berry' }].map((s) => (
                <button key={s.c} onClick={() => setLipColor(s.c)} title={s.l} className="w-5 h-5 rounded-full border border-white/30 hover:scale-110 transition" style={{ background: s.c }} />
              ))}
              {lipColor && <button onClick={() => setLipColor('')} data-testid="camera-lip-clear" className="text-[10px] text-red-300 hover:text-red-100 ml-0.5">✕</button>}
            </div>
            <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-2 py-1 border border-amber-400/30">
              <label className="text-[10px] font-bold text-amber-300">💇 Hair:</label>
              <input type="color" value={hairColor || '#3b1f10'} onChange={(e) => setHairColor(e.target.value)} data-testid="camera-hair-color" className="w-7 h-7 rounded cursor-pointer bg-transparent border-0" />
              {[{ c: '#3b1f10', l: 'Brown' }, { c: '#0f0f0f', l: 'Black' }, { c: '#a0522d', l: 'Auburn' }, { c: '#d4a574', l: 'Blonde' }, { c: '#8b0000', l: 'Burgundy' }, { c: '#7c3aed', l: 'Violet' }].map((s) => (
                <button key={s.c} onClick={() => setHairColor(s.c)} title={s.l} className="w-5 h-5 rounded-full border border-white/30 hover:scale-110 transition" style={{ background: s.c }} />
              ))}
              {hairColor && <button onClick={() => setHairColor('')} data-testid="camera-hair-clear" className="text-[10px] text-red-300 hover:text-red-100 ml-0.5">✕</button>}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">Choose Product ({products.length} available)</div>
          {ready && products.length > 0 && (
            <AIBestMatchButton canvasRef={canvasRef} category={category} onPicks={(ids) => setAiPicks(ids)} />
          )}
        </div>
        {products.length === 0 ? (
          <div className="text-xs text-slate-500 italic text-center py-2">Koi product available nahi hai. Admin panel se add karein.</div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {products.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                data-testid={`camera-product-${p.id}`}
                className={`flex-shrink-0 w-16 rounded-lg overflow-hidden border-2 transition relative ${selected?.id === p.id ? 'border-cyan-400 ring-2 ring-cyan-400/50' : aiPicks.includes(p.id) ? 'border-amber-400 ring-2 ring-amber-400/60 animate-pulse' : 'border-white/10 hover:border-white/30'}`}>
                {aiPicks.includes(p.id) && <div className="absolute top-0 right-0 text-[7px] px-1 py-0 rounded-bl bg-amber-500 text-black font-black z-10">AI</div>}
                <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-16 object-cover bg-slate-800" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                <div className="text-[8px] text-white truncate p-0.5 bg-black/50">{p.name}</div>
              </button>
            ))}
            {selected && (
              <button onClick={() => setSelected(null)} className="flex-shrink-0 w-16 h-16 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-200 text-[10px] font-bold flex flex-col items-center justify-center border-2 border-red-400/40">
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        )}
        <div className="text-[9px] text-slate-500 text-center mt-1"><Sparkles className="w-3 h-3 inline text-cyan-400" /> MediaPipe FaceMesh · 100% browser-side · Privacy-first</div>
      </div>
    </div>
  );
};

export default TrialRoomLiveCamera;
